// Loads Google tag and Hotjar. Only call these after the visitor chose "granted":
// basic consent mode means nothing from either vendor is requested before that.
// Both load in production builds only, so dev and test sign-ups never count.
import { getConsent } from 'lib/consent';
import {
  GA4_ID,
  GADS_SIGNUP_SEND_TO,
  GOOGLE_ADS_ID,
  HOTJAR_ID,
} from 'lib/trackingConfig';

const GOOGLE_TAG_SCRIPT_ID = 'skiloope-google-tag';
const HOTJAR_SCRIPT_ID = 'skiloope-hotjar';
const HOTJAR_VERSION = 6;

type ConsentState = 'granted' | 'denied';

interface ITrackingWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  hj?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  _hjSettings?: { hjid: number; hjsv: number };
}

const GOOGLE_CONSENT_TYPES = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
] as const;

const consentState = (state: ConsentState): Record<string, ConsentState> =>
  Object.fromEntries(GOOGLE_CONSENT_TYPES.map(type => [type, state]));

const isProduction = (): boolean => process.env.NODE_ENV === 'production';

const loadGoogleTag = (): void => {
  const trackingWindow = window as ITrackingWindow;
  // Re-renders and repeated Accept clicks must not load or configure it twice.
  if (document.getElementById(GOOGLE_TAG_SCRIPT_ID)) return;

  trackingWindow.dataLayer = trackingWindow.dataLayer || [];
  // gtag.js only understands the `arguments` object, not an array of args.
  trackingWindow.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    trackingWindow.dataLayer?.push(arguments);
  };
  const { gtag } = trackingWindow;

  gtag('consent', 'default', consentState('denied'));
  gtag('consent', 'update', consentState('granted'));
  gtag('js', new Date());
  gtag('config', GA4_ID);
  gtag('config', GOOGLE_ADS_ID);

  const script = document.createElement('script');
  script.id = GOOGLE_TAG_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);
};

const loadHotjar = (): void => {
  const trackingWindow = window as ITrackingWindow;
  if (document.getElementById(HOTJAR_SCRIPT_ID)) return;

  trackingWindow.hj =
    trackingWindow.hj ||
    function hj(...args: unknown[]) {
      if (!trackingWindow.hj) return;
      trackingWindow.hj.q = trackingWindow.hj.q || [];
      trackingWindow.hj.q.push(args);
    };
  trackingWindow._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_VERSION };

  const script = document.createElement('script');
  script.id = HOTJAR_SCRIPT_ID;
  script.async = true;
  script.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=${HOTJAR_VERSION}`;
  document.head.appendChild(script);
};

export const loadTracking = (): void => {
  if (!isProduction()) return;
  loadGoogleTag();
  loadHotjar();
};

// Scripts can't be unloaded in place, so callers reload the page after this.
export const denyGoogleConsent = (): void => {
  (window as ITrackingWindow).gtag?.('consent', 'update', consentState('denied'));
};

export const isTrackingLoaded = (): boolean =>
  Boolean(
    document.getElementById(GOOGLE_TAG_SCRIPT_ID) ||
      document.getElementById(HOTJAR_SCRIPT_ID),
  );

// Fire-and-forget: tracking must never break or delay registration.
export const trackSignUpConversion = (): void => {
  try {
    if (getConsent() !== 'granted') return;
    (window as ITrackingWindow).gtag?.('event', 'conversion', {
      send_to: GADS_SIGNUP_SEND_TO,
    });
  } catch {
    // Intentionally ignored.
  }
};
